import React, { Fragment, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { faBars, faChevronDown } from "@fortawesome/pro-light-svg-icons";
import { IconProp } from "@fortawesome/fontawesome-svg-core"; // Import IconProp for FontAwesome icons

import { Userpilot } from "userpilot";
import { paths } from "~/router/paths";
import { navItems } from "~/constants/sidebar";
import { prefetchRoute } from "~/helpers/routePrefetch";
import { DISABLE_PAYMENTS } from "~/helpers/plans";

import { useStore } from "~/hooks/useStore";
import { useAccount } from "~/hooks/useAccount";
import { useMediaQuery } from "~/hooks/useMediaQuery";

import * as S from "./Sidebar.style";
import Button from "~/components/ui/Button";
import FlexContainer from "~/components/ui/FlexContainer";
import { SVG } from "~/components/ui/SVG";
import { SVGIcon } from "~/components/ui/SVG/types";
import { StyledLink } from "~/components/ui/Styled/Link";
import { Notifications } from "~/components/notifications";
import { HelpCenterModal } from "~/components/helpCenterModal";

import { UseQueryResult } from "@tanstack/react-query";

// @ts-ignore
import LogoSvg from "~/assets/svg/logo.svg?react";
import { useDesktopSidebar } from "~/contexts/DesktopSidebarContext";

import StatIconMark from "~/assets/svg/stat.icon.svg";
import SidebarPanelIcon from "~/assets/svg/sidebar-icon.svg";

export interface INavItem {
  namePath: string;
  path?: string;
  /** FontAwesome icon, or `{ default, active }` image URLs for parent rows with children */
  icon?: SVGIcon | { default: string; active: string };
  iconImage?: string;
  activeIconImage?: string;
  openInNewTab?: boolean;
  getCountQuery?: () => UseQueryResult<{ count: number }, unknown>;
  showHelpCenterModal?: boolean;
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
  children?: INavItem[];
  sectionLabel?: string;
  navExact?: boolean;
}

let _userpilotInitialized = false;
const initUserpilot = () => {
  if (!_userpilotInitialized) {
    Userpilot.initialize("NX-f9dabb4f");
    _userpilotInitialized = true;
  }
};

export const Brand = ({ collapsed }: { collapsed?: boolean }) => (
  <S.LinkContainer>
    <StyledLink to={paths.app.home}>
      <S.BrandContainer data-collapsed={collapsed ? "true" : undefined}>
        <S.BrandMark $compact={!!collapsed}>
          <img src={StatIconMark} alt="" width={21} height={21} />
        </S.BrandMark>
        <S.BrandWordmark className="brand-wordmark">
          Dropshiptool.io
        </S.BrandWordmark>
      </S.BrandContainer>
    </StyledLink>
  </S.LinkContainer>
);

interface MenuItemProps extends INavItem {
  openHelpCenter?: () => void;
  closeMobileSidebar?: () => void;
  closeOpenMenus?: () => void;
  closeOpenMenusSignal?: number;
  /** Desktop collapsed rail: icon-only; expanding opens full sidebar for nested items. */
  navCompact?: boolean;
  onExpandSidebar?: () => void;
}

const isCustomSidebarIcon = (
  icon: unknown
): icon is { default: string; active: string } => {
  return (
    !!icon &&
    typeof icon === "object" &&
    "default" in (icon as Record<string, unknown>) &&
    "active" in (icon as Record<string, unknown>)
  );
};

const MenuItem: React.FC<MenuItemProps> = ({
  path,
  openInNewTab,
  icon,
  iconImage,
  activeIconImage,
  sectionLabel,
  namePath,
  showHelpCenterModal,
  openHelpCenter,
  closeMobileSidebar,
  closeOpenMenus,
  closeOpenMenusSignal = 0,
  onClick,
  children,
  getCountQuery,
  navCompact,
  onExpandSidebar,
  navExact = true,
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const { store } = useStore();
  const queryResult = getCountQuery?.();
  const count = queryResult?.data?.count ?? 0;
  const [isOpen, setIsOpen] = useState(false);
  const hasActiveChild = useMemo(() => {
    if (!children?.length) return false;
    return children.some((child) =>
      child.path ? location.pathname.startsWith(child.path) : false
    );
  }, [children, location.pathname]);
  const shouldUseActiveIcon = isOpen || hasActiveChild;

  useEffect(() => {
    if (navCompact) setIsOpen(false);
  }, [navCompact]);

  useEffect(() => {
    if (!children?.length || navCompact) return;
    setIsOpen(hasActiveChild);
  }, [children?.length, hasActiveChild, location.pathname, navCompact]);

  useEffect(() => {
    if (!children?.length || hasActiveChild) return;
    setIsOpen(false);
  }, [children?.length, closeOpenMenusSignal, hasActiveChild]);

  if (store && namePath === "nav.connect-store") {
    return null;
  }

  const buttonStyle = {
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    width: "100%",
  };

  const childrenButtonStyle = {
    ...buttonStyle,
    marginBottom: 5,
  };

  const renderMenuIcon = (isActive = false) => {
    const iconSrc = isActive && activeIconImage ? activeIconImage : iconImage;
    if (iconSrc) {
      return <img src={iconSrc} alt="" aria-hidden width={18} height={18} />;
    }
    if (icon && isCustomSidebarIcon(icon)) {
      return (
        <img
          src={isActive ? icon.active : icon.default}
          alt=""
          aria-hidden
          width={18}
          height={18}
        />
      );
    }
    if (icon) {
      return <SVG icon={icon} svgProp={{ width: 18 }} />;
    }
    return null;
  };

  const renderChildren = () => (
    <S.RenderContainer className="sidebar-submenu">
      {children?.map((childItem, idx) => (
        <S.NavItem
          key={idx}
          to={childItem.path ?? "#"}
          end
          target={childItem.openInNewTab ? "_blank" : undefined}
          rel={childItem.openInNewTab ? "noopener noreferrer" : undefined}
          style={{ textDecoration: "none", width: "100%" }}
          onMouseEnter={() => childItem.path && prefetchRoute(childItem.path)}
          onClick={(e) => {
            closeMobileSidebar?.();
            if (showHelpCenterModal && openHelpCenter) {
              e.preventDefault();
              openHelpCenter();
            }
            if (onClick) {
              e.preventDefault();
              onClick(e);
            }
          }}>
          {({ isActive }) => (
            <Button
              data-ischildren={true}
              variant={isActive ? "filled" : "default"}
              color={isActive ? "primary" : "text"}
              fontWeight={isActive ? 600 : 500}
              bgColor={isActive ? "primaryLight" : "white"}
              radius={0.75}
              gap="20px"
              style={buttonStyle}
              justifyContent="flex-start">
              <S.NavItemLabel>{t(childItem.namePath)}</S.NavItemLabel>
            </Button>
          )}
        </S.NavItem>
      ))}
    </S.RenderContainer>
  );

  return (
    <Fragment>
      {sectionLabel && <S.MenuItemTitle>{t(sectionLabel)}</S.MenuItemTitle>}
      {children?.length ? (
        <>
          <S.ButtonWithChildren
            variant={hasActiveChild ? "filled" : "default"}
            color={hasActiveChild ? "primary" : "text"}
            fontWeight={400}
            bgColor={hasActiveChild ? "primaryLight" : "white"}
            radius={0.75}
            gap="26px"
            style={childrenButtonStyle}
            justifyContent="flex-start"
            title={navCompact ? String(t(namePath)) : undefined}
            onClick={() => {
              if (navCompact) {
                onExpandSidebar?.();
                if (!hasActiveChild && children?.[0]?.path) {
                  navigate(children[0].path);
                }
                setIsOpen(true);
                return;
              }
              if (!hasActiveChild && children?.[0]?.path) {
                navigate(children[0].path);
                setIsOpen(true);
                return;
              }
              setIsOpen(!isOpen);
            }}>
            {renderMenuIcon(shouldUseActiveIcon) ??
              (icon && !isCustomSidebarIcon(icon) ? (
                <SVG icon={icon as SVGIcon} svgProp={{ width: 18 }} />
              ) : null)}
            <S.NavItemLabel>{t(namePath)}</S.NavItemLabel>

            <S.CollapseIcon
              className="sidebar-collapse-chevron"
              isOpen={isOpen}>
              <SVG icon={faChevronDown} svgProp={{ width: 12 }} />
            </S.CollapseIcon>
          </S.ButtonWithChildren>
          {isOpen && !navCompact && renderChildren()}
        </>
      ) : (
        <S.NavItem
          to={path ?? "#"}
          end={navExact}
          target={openInNewTab ? "_blank" : undefined}
          rel={openInNewTab ? "noopener noreferrer" : undefined}
          onMouseEnter={() => path && prefetchRoute(path)}
          onClick={(e) => {
            closeMobileSidebar?.();
            closeOpenMenus?.();
            if (showHelpCenterModal && openHelpCenter) {
              e.preventDefault();
              openHelpCenter();
            }
            if (onClick) {
              e.preventDefault();
              onClick(e);
            }
          }}>
          {({ isActive }) => (
            <Button
              variant={isActive ? "filled" : "default"}
              color={isActive ? "primary" : "text"}
              fontWeight={isActive ? 600 : 500}
              bgColor={isActive ? "primaryLight" : "white"}
              radius={0.75}
              gap="26px"
              style={buttonStyle}
              justifyContent="flex-start"
              title={navCompact ? String(t(namePath)) : undefined}>
              {renderMenuIcon(isActive) ??
                (icon && !isCustomSidebarIcon(icon) ? (
                  <SVG icon={icon as SVGIcon} svgProp={{ width: 18 }} />
                ) : null)}
              <S.NavItemLabel>{t(namePath)}</S.NavItemLabel>
            </Button>
          )}
        </S.NavItem>
      )}
    </Fragment>
  );
};

const Menu: React.FC<{ closeMobileSidebar?: () => void }> = ({
  closeMobileSidebar,
}) => {
  const location = useLocation();
  const { account } = useAccount();
  const [showHelpCenter, setShowHelpCenter] = useState(false);
  const [closeOpenMenusSignal, setCloseOpenMenusSignal] = useState(0);
  const isTabletAndUp = useMediaQuery("tablet");
  const { collapsed, setCollapsed } = useDesktopSidebar();
  const navCompact = isTabletAndUp && collapsed;

  useEffect(() => {
    if (account) {
      Userpilot.reload();
    }
  }, [account, location]);

  const settingsIndex = useMemo(
    () =>
      navItems.findIndex(({ path }) =>
        DISABLE_PAYMENTS
          ? path === paths.settings.account
          : path === paths.settings.plans
      ),
    []
  );

  const [itemsBeforeSettings, itemsAfterSettings] = useMemo(() => {
    if (settingsIndex < 0) return [navItems, []];
    return [navItems.slice(0, settingsIndex), navItems.slice(settingsIndex)];
  }, [settingsIndex]);

  const helpCenterPath = useMemo(
    () => navItems.find(({ showHelpCenterModal }) => showHelpCenterModal)?.path,
    []
  );

  return (
    <S.NavContainer className="sidebar-nav-root">
      <FlexContainer
        height="100%"
        width="100%"
        flexDirection="column"
        justifyContent="space-between">
        <S.MenuItensContainer gap={0}>
          {itemsBeforeSettings.map((navItem) => (
            <MenuItem
              key={navItem.namePath}
              closeMobileSidebar={closeMobileSidebar}
              closeOpenMenus={() => setCloseOpenMenusSignal((prev) => prev + 1)}
              closeOpenMenusSignal={closeOpenMenusSignal}
              navCompact={navCompact}
              onExpandSidebar={() => setCollapsed(false)}
              {...navItem}
            />
          ))}
        </S.MenuItensContainer>

        <S.MenuItensContainer>
          {itemsAfterSettings.map((navItem) => (
            <MenuItem
              key={navItem.namePath}
              openHelpCenter={() => setShowHelpCenter(true)}
              closeMobileSidebar={closeMobileSidebar}
              closeOpenMenus={() => setCloseOpenMenusSignal((prev) => prev + 1)}
              closeOpenMenusSignal={closeOpenMenusSignal}
              navCompact={navCompact}
              onExpandSidebar={() => setCollapsed(false)}
              {...navItem}
            />
          ))}
        </S.MenuItensContainer>
      </FlexContainer>

      <HelpCenterModal
        path={String(helpCenterPath)}
        isShowing={showHelpCenter}
        hide={() => setShowHelpCenter(false)}
      />
    </S.NavContainer>
  );
};

const DesktopSidebar: React.FC = () => {
  const { collapsed, toggleCollapsed } = useDesktopSidebar();

  return (
    <S.SidebarContainer data-collapsed={collapsed ? "true" : undefined}>
      <S.DesktopSidebarHeader data-collapsed={collapsed ? "true" : undefined}>
        <Brand collapsed={collapsed} />
        <S.DesktopSidebarIconButton
          type="button"
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          aria-expanded={!collapsed}
          onClick={toggleCollapsed}>
          <img src={SidebarPanelIcon} alt="" width={17} height={17} />
        </S.DesktopSidebarIconButton>
      </S.DesktopSidebarHeader>
      <Menu />
    </S.SidebarContainer>
  );
};

const MobileSidebar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <S.SidebarContainer>
      <Notifications />
      <Brand />
      <S.MobileMenuButton onClick={() => setIsOpen(!isOpen)}>
        <SVG icon={faBars as SVGIcon} size="lg" />{" "}
      </S.MobileMenuButton>
      {isOpen && (
        <S.MobileMenu>
          <Menu closeMobileSidebar={() => setIsOpen(false)} />
        </S.MobileMenu>
      )}
    </S.SidebarContainer>
  );
};

const Sidebar: React.FC = () => {
  useEffect(() => {
    initUserpilot();
  }, []);

  const isTabletAndUp = useMediaQuery("tablet");
  return isTabletAndUp ? <DesktopSidebar /> : <MobileSidebar />;
};

export default Sidebar;
